'use client';

import { useId, useState } from 'react';
import type { CSSProperties } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import type { Variants } from 'motion/react';
import {
  RiArrowGoBackLine,
  RiCalendarScheduleLine,
  RiEyeLine,
  RiRepeat2Line,
  RiRobot2Line,
  RiSendPlaneLine,
} from '@remixicon/react';
import { useTranslations } from 'next-intl';

import { ProductSection, SectionHead } from '@/components/product-section';
import { dur, spring, springFade } from '@/lib/motion';
import { hl } from '@/lib/rich-text';

import './autopilot.css';

/**
 * "Ona bir iş verin" — Operatör görevleri.
 *
 * Solda sohbetten üç örnek istek, sağda Operatör'ün görev listesi. İstek
 * gönderildikçe listeye kendi TÜRÜYLE düşüyor. Türler ürünün kendisi
 * (`@mentorivo/shared/operator` → FOLLOW_UP / RECURRING / WATCH):
 *   takip    5 dakika ile 90 gün sonrası arasında bir kez,
 *   tekrar   en sık 30 dakikada bir,
 *   izle     bir koşul; varsayılan 4 saatte bir bakar.
 * Görev kurmak onay istemiyor — görev bir NOT; içinde bir değişiklik
 * gerekirse o değişiklik moda göre davranıyor (Öner'de onaya düşer).
 *
 * Hareket: gönderilen istek listenin TEPESİNE iniyor ve alttakiler yer
 * açıyor (`layout`) — "listeye eklendi" sözle değil konumla anlatılıyor.
 * Gönderilen istek düğmesi soluklaşıyor; aynı görev iki kez kurulmuyor.
 */

const TASKS = ['followUp', 'recurring', 'watch'] as const;
type TaskKey = (typeof TASKS)[number];

const TYPE_ICON: Record<TaskKey, typeof RiEyeLine> = {
  followUp: RiCalendarScheduleLine,
  recurring: RiRepeat2Line,
  watch: RiEyeLine,
};

const taskV: Variants = {
  hidden: { opacity: 0, y: -10, filter: 'blur(4px)' },
  show: { opacity: 1, y: 0, filter: 'blur(0px)', transition: springFade(spring.default) },
  exit: { opacity: 0, transition: { duration: dur.instant } },
};

export function AutopilotTasks() {
  const t = useTranslations('featuresAutopilot.tasks');
  const uid = useId();
  const [sent, setSent] = useState<TaskKey[]>([]);

  const send = (key: TaskKey) =>
    setSent((current) => (current.includes(key) ? current : [key, ...current]));

  return (
    <ProductSection tone="white" labelledBy={`${uid}-title`}>
      <SectionHead
        id={`${uid}-title`}
        eyebrow={t('eyebrow')}
        title={t.rich('title', { hl })}
        lead={t('lead')}
      />

      <div className="apt">
        <div className="apt-chat">
          <p className="apt-head">
            <RiRobot2Line size={15} aria-hidden="true" />
            {t('chatHead')}
          </p>
          <ul className="apt-prompts">
            {TASKS.map((key) => {
              const done = sent.includes(key);
              return (
                <li key={key}>
                  <button
                    type="button"
                    className="apt-prompt"
                    onClick={() => send(key)}
                    disabled={done}
                    aria-controls={`${uid}-tasks`}
                  >
                    <span className="apt-prompt-text">{t(`items.${key}.ask`)}</span>
                    <span className="apt-prompt-send" aria-hidden="true">
                      <RiSendPlaneLine size={15} />
                    </span>
                    <span className="sr-only">{done ? t('sent') : t('send')}</span>
                  </button>
                </li>
              );
            })}
          </ul>
          <p className="apt-hint">{t('hint')}</p>
        </div>

        <div className="apt-board">
          <div className="apt-board-top">
            <p className="apt-head">{t('boardHead')}</p>
            <span className="apt-count">{t('count', { count: sent.length })}</span>
          </div>

          <motion.ol className="apt-tasks" id={`${uid}-tasks`} aria-live="polite" layout>
            <AnimatePresence initial={false}>
              {sent.length === 0 ? (
                <motion.li
                  key="empty"
                  className="apt-empty"
                  variants={taskV}
                  initial="hidden"
                  animate="show"
                  exit="exit"
                  layout
                >
                  {t('empty')}
                </motion.li>
              ) : (
                sent.map((key) => {
                  const Icon = TYPE_ICON[key];
                  return (
                    <motion.li
                      key={key}
                      className={`apt-task apt-task--${key}`}
                      variants={taskV}
                      initial="hidden"
                      animate="show"
                      exit="exit"
                      layout
                    >
                      <span className="apt-type">
                        <Icon size={14} aria-hidden="true" />
                        {t(`items.${key}.type`)}
                      </span>
                      <p className="apt-task-title">{t(`items.${key}.task`)}</p>
                      <p className="apt-task-when">{t(`items.${key}.when`)}</p>
                      <p className="apt-task-rule">{t(`items.${key}.rule`)}</p>
                    </motion.li>
                  );
                })
              )}
            </AnimatePresence>
          </motion.ol>

          {sent.length > 0 && (
            <button type="button" className="apt-reset" onClick={() => setSent([])}>
              <RiArrowGoBackLine size={14} aria-hidden="true" />
              {t('reset')}
            </button>
          )}
        </div>
      </div>

      <ul className="ps-facts" style={{ '--ps-facts': 3 } as CSSProperties}>
        {(['limit', 'team', 'off'] as const).map((key) => (
          <li key={key} className="ps-fact">
            <p className="ps-fact-title">{t(`facts.${key}.title`)}</p>
            <p className="ps-fact-text">{t(`facts.${key}.text`)}</p>
          </li>
        ))}
      </ul>
    </ProductSection>
  );
}
